import { useState, type FormEvent } from 'react';
import { api, describeError } from '../../api.ts';
import { runWrite } from '../../ui/changes.ts';
import { useToast } from '../../ui/toasts.tsx';
import { MerchantPicker, SiteSelect } from './pickers.tsx';
import { ROLE_LABELS, type LinkAnalysis, type Named, type NetworkDeeplinks } from './types.ts';

/** Paste sample tracking links → detected parameters and values → save with one click. */
export function LinkExtraction({ data }: { data: NetworkDeeplinks }) {
  const toast = useToast();
  const [links, setLinks] = useState('');
  const [siteRef, setSiteRef] = useState(data.sites[0]?.ref ?? '');
  const [merchant, setMerchant] = useState<Named | null>(null);
  const [analysis, setAnalysis] = useState<LinkAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [skip, setSkip] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const site = data.sites.find((s) => s.ref === siteRef);

  const analyze = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.op<LinkAnalysis>('admin.deeplinks.analyze', {
        networkRef: data.network.ref,
        links: links.split(/\s+/).filter(Boolean).slice(0, 10),
        ...(siteRef ? { siteRef } : {}),
        ...(merchant ? { merchantRef: merchant.ref } : {}),
      });
      setAnalysis(result);
      // Existing parameters stay as they are, and a value that would replace a different stored one is opt-in.
      setSkip(
        new Set([
          ...result.parameters.filter((p) => p.existing !== 'new' && !(p.existing === 'different' && p.fromProgram)).map((p) => `p:${p.parameter}`),
          ...result.values.filter((v) => v.current !== null && v.current !== v.value).map((v) => `v:${v.parameter}`),
        ]),
      );
    } catch (err) {
      setAnalysis(null);
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (key: string) => setSkip((s) => {
    const next = new Set(s);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
  const chosenParams = analysis?.parameters.filter((p) => p.existing === 'new' && !skip.has(`p:${p.parameter}`)) ?? [];
  // Existing merchant id parameters that would switch to "filled from the program".
  const switchable = (p: LinkAnalysis['parameters'][number]) => p.existing === 'different' && p.fromProgram;
  const chosenSwitches = analysis?.parameters.filter((p) => switchable(p) && !skip.has(`p:${p.parameter}`)) ?? [];
  const paramKept = (name: string) => {
    const p = analysis?.parameters.find((x) => x.parameter === name);
    return p !== undefined && (p.existing !== 'new' || !skip.has(`p:${name}`));
  };
  const chosenValues = analysis?.values.filter((v) => paramKept(v.parameter) && !skip.has(`v:${v.parameter}`) && v.current !== v.value) ?? [];
  const setGenerator = analysis && !analysis.currentGenerator ? analysis.suggestedGenerator.key : null;

  const save = async () => {
    if (!analysis) return;
    setBusy(true);
    const result = await runWrite<{ createdParameters: string[]; valuesSet: number }>(
      toast,
      'admin.deeplinks.applyExtraction',
      {
        networkRef: data.network.ref,
        generatorKey: setGenerator,
        parameters: [...chosenParams, ...chosenSwitches].map(({ parameter, parameterType, level, isOptional, fromProgram }) => ({ parameter, parameterType, level, isOptional, fromProgram })),
        values: chosenValues.map(({ parameter, value, siteRef: s, merchantRef }) => ({ parameter, value, siteRef: s, merchantRef })),
      },
      (r) => `Saved ${r.createdParameters.length} parameter(s) and ${r.valuesSet} value(s)`,
    );
    setBusy(false);
    if (result) setAnalysis(null);
  };

  return (
    <div className="aff-steps">
      <form className="card" onSubmit={analyze} aria-label="Read parameters from links">
        <h2>Sample tracking links</h2>
        <p className="muted small">Paste one or more links you created in the network’s dashboard for one site (and, ideally, one merchant). Several links help tell fixed parts from ids.</p>
        <textarea className="mono" rows={6} value={links} onChange={(e) => setLinks(e.target.value)} aria-label="Tracking links" placeholder="https://www.awin1.com/cread.php?awinmid=…&awinaffid=…&ued=…" />
        <div className="form-grid">
          <SiteSelect sites={data.sites} value={siteRef} onChange={setSiteRef} label="Links are for site" />
          <MerchantPicker value={merchant} onChange={setMerchant} market={site?.country} label="Links point to merchant" />
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy || links.trim() === ''}>
          Read links
        </button>
        {error ? <p className="text-warn">{error}</p> : null}
      </form>
      <div className="card" aria-live="polite">
        <h2>Detected</h2>
        {!analysis ? <p className="muted">Results appear here. Nothing is saved until you click Save.</p> : null}
        {analysis ? (
          <>
            <p>
              <strong>{analysis.familyLabel ?? 'Unknown network'}</strong> · <span className="mono">{analysis.host}{analysis.pathTemplate}</span>
              <br />
              <span className="muted small">
                Link builder: {analysis.suggestedGenerator.label}
                {analysis.currentGenerator ? ` (network uses ${analysis.currentGenerator})` : ' (will be set)'}
              </span>
            </p>
            {analysis.warnings.length ? (
              <ul className="warnings">
                {analysis.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            ) : null}
            <div style={{ overflowX: 'auto' }}>
            <table className="table" data-extraction-parameters>
              <thead>
                <tr>
                  <th scope="col" aria-label="Save" />
                  <th scope="col">Parameter</th>
                  <th scope="col">Means</th>
                  <th scope="col">Level</th>
                  <th scope="col">Value</th>
                </tr>
              </thead>
              <tbody>
                {analysis.parameters.map((p) => {
                  const value = analysis.values.find((v) => v.parameter === p.parameter);
                  const part = analysis.parts.find((x) => x.name === p.parameter);
                  return (
                    <tr key={p.parameter}>
                      <td>
                        {p.existing === 'new' || switchable(p) ? (
                          <input
                            type="checkbox"
                            checked={!skip.has(`p:${p.parameter}`)}
                            onChange={() => toggle(`p:${p.parameter}`)}
                            aria-label={switchable(p) ? `Fill ${p.parameter} from the program` : `Add ${p.parameter}`}
                            title={switchable(p) ? 'Exists; switch it to the program id' : undefined}
                          />
                        ) : (
                          <span className={`badge ${p.existing === 'same' ? 'badge-ok' : 'badge-warn'}`} title={p.existing === 'different' ? `exists at level ${p.existingLevel}` : 'already set up'}>
                            {p.existing === 'same' ? 'exists' : 'differs'}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="mono">{p.parameter}</span> <small className="muted">{p.parameterType}</small>
                      </td>
                      <td className="aff-role" title={part?.reason}>
                        {ROLE_LABELS[p.role]}
                        {part?.varies ? <small className="muted"> · varies</small> : null}
                      </td>
                      <td>{p.fromProgram ? 'From program' : p.levelLabel}</td>
                      <td>
                        {p.fromProgram ? (
                          <ProgramMatches matches={analysis.programMatches.filter((m) => m.parameter === p.parameter)} />
                        ) : value ? (
                          <label className="inline-check">
                            <input type="checkbox" checked={!skip.has(`v:${p.parameter}`) && value.current !== value.value} disabled={value.current === value.value} onChange={() => toggle(`v:${p.parameter}`)} aria-label={`Save value of ${p.parameter}`} />
                            <span className="mono truncate" style={{ maxWidth: 220, display: 'inline-block', verticalAlign: 'bottom' }} title={value.value}>
                              {value.value}
                            </span>
                            <small className="muted">
                              {value.scopeLabel}
                              {value.current === value.value ? ' · already stored' : value.current ? ` · replaces ${value.current}` : ''}
                            </small>
                          </label>
                        ) : (
                          <span className="muted small">{p.role === 'target_url' ? 'deep URL per link' : p.role === 'click_ref' ? 'click id per click' : '—'}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-primary" disabled={busy || (chosenParams.length === 0 && chosenSwitches.length === 0 && chosenValues.length === 0 && !setGenerator)} onClick={save}>
                Save {chosenParams.length + chosenSwitches.length} parameter(s) and {chosenValues.length} value(s)
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/** For a merchant id in the links: no value is stored, each program's own id is used; says which program has this one. */
function ProgramMatches({ matches }: { matches: LinkAnalysis['programMatches'] }) {
  return (
    <span className="small">
      <span className="muted">each program’s id</span>
      {matches.map((m) => (
        <span key={m.value} className="aff-match">
          <span className="mono">{m.value}</span>{' '}
          {m.program ? (
            <span className="badge badge-ok" title={m.program.merchant ?? undefined}>
              {m.program.name}
            </span>
          ) : (
            <span className="badge badge-warn" title="No program on this network has this id yet">no program</span>
          )}
        </span>
      ))}
    </span>
  );
}
